/**
 * Integrations Page
 * Modern, professional MCP integrations management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Link2, CheckCircle2, Settings2, Zap, 
  ChevronDown, ExternalLink, RefreshCw, Unplug,
  Shield, Database, GitBranch, MessageSquare, FileText,
  AlertCircle, Check, Search, Plus, Edit2, Trash2
} from 'lucide-react';
import { 
  JiraIcon, ConfluenceIcon, GitHubIcon, 
  AzureDevOpsIcon, SlackIcon, TeamsIcon,
} from '../../components/common/BrandIcons';
import { AppLayout } from '../../components/layout';
import { KnowledgeSidebar } from '../KnowledgeBasePage/components/KnowledgeSidebar';
import { ConfirmModal, Spinner, Input, Button } from '../../components/ui';
import { CustomIntegrationForm } from '../../components/integrations';
import api, { MCPServer, MCPOAuthStatus, MCPToolResult, Assistant } from '../../services/api';
import { CustomIntegration } from '../../types';
import styles from './IntegrationsPage.module.css';

interface IntegrationConfig {
  name: string;
  provider: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bgGradient: string;
  authType: 'oauth' | 'pat';
  category: 'project' | 'docs' | 'code' | 'communication';
  features: string[];
}

const INTEGRATIONS: IntegrationConfig[] = [
  {
    name: 'GitHub',
    provider: 'github',
    description: 'Manage repositories, pull requests, and CI/CD workflows',
    icon: <GitHubIcon size={24} />,
    color: '#24292E',
    bgGradient: 'linear-gradient(135deg, #24292E 0%, #4A4A4A 100%)',
    authType: 'pat',
    category: 'code',
    features: ['Repos', 'PRs', 'Issues', 'Actions'],
  },
  {
    name: 'Jira',
    provider: 'jira',
    description: 'Track issues, manage sprints, and automate workflows',
    icon: <JiraIcon size={24} />,
    color: '#0052CC',
    bgGradient: 'linear-gradient(135deg, #0052CC 0%, #2684FF 100%)',
    authType: 'oauth',
    category: 'project',
    features: ['Issues', 'Projects', 'Sprints', 'Workflows'],
  },
  {
    name: 'Confluence',
    provider: 'confluence',
    description: 'Access documentation, wikis, and team knowledge bases',
    icon: <ConfluenceIcon size={24} />,
    color: '#172B4D',
    bgGradient: 'linear-gradient(135deg, #172B4D 0%, #344563 100%)',
    authType: 'oauth',
    category: 'docs',
    features: ['Pages', 'Spaces', 'Comments', 'Attachments'],
  },
  {
    name: 'Slack',
    provider: 'slack',
    description: 'Send messages, search channels, and integrate bots',
    icon: <SlackIcon size={24} />,
    color: '#4A154B',
    bgGradient: 'linear-gradient(135deg, #4A154B 0%, #7C3085 100%)',
    authType: 'oauth',
    category: 'communication',
    features: ['Messages', 'Channels', 'Users', 'Reactions'],
  },
  {
    name: 'Azure DevOps',
    provider: 'azure-devops',
    description: 'Connect work items, pipelines, and Azure repos',
    icon: <AzureDevOpsIcon size={24} />,
    color: '#0078D4',
    bgGradient: 'linear-gradient(135deg, #0078D4 0%, #00BCF2 100%)',
    authType: 'pat',
    category: 'project',
    features: ['Work Items', 'Pipelines', 'Repos', 'Wiki'],
  },
  {
    name: 'Teams',
    provider: 'teams',
    description: 'Connect channels and workspace communication',
    icon: <TeamsIcon size={24} />,
    color: '#6264A7',
    bgGradient: 'linear-gradient(135deg, #6264A7 0%, #7B83EB 100%)',
    authType: 'oauth',
    category: 'communication',
    features: ['Meetings', 'Recording', 'Transcripts', 'MoM'],
  },
];

const CATEGORIES = {
  project: { label: 'Project Management', icon: <Database size={16} /> },
  docs: { label: 'Documentation', icon: <FileText size={16} /> },
  code: { label: 'Source Control', icon: <GitBranch size={16} /> },
  communication: { label: 'Communication', icon: <MessageSquare size={16} /> },
};

export interface IntegrationsPageProps {
  embedded?: boolean;
}

export const IntegrationsPage: React.FC<IntegrationsPageProps> = ({ embedded = false }) => {
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<Record<string, MCPOAuthStatus>>({});
  const [loading, setLoading] = useState(true);
  const [testResults, setTestResults] = useState<Record<string, MCPToolResult | null>>({});
  const [testingProvider, setTestingProvider] = useState<string | null>(null);
  const [syncingProvider, setSyncingProvider] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  
  // Assistants for sync selection
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [selectedAssistants, setSelectedAssistants] = useState<Record<string, string[]>>({});
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  
  // Azure DevOps PAT modal
  const [showAdoModal, setShowAdoModal] = useState(false);
  const [adoOrg, setAdoOrg] = useState('');
  const [adoPat, setAdoPat] = useState('');
  const [adoError, setAdoError] = useState('');
  const [adoConnecting, setAdoConnecting] = useState(false);

  // GitHub PAT modal
  const [showGithubModal, setShowGithubModal] = useState(false);
  const [githubPat, setGithubPat] = useState('');
  const [githubError, setGithubError] = useState('');
  const [githubConnecting, setGithubConnecting] = useState(false);
  
  // Disconnect confirmation modal
  const [disconnectModal, setDisconnectModal] = useState<{ provider: string; name: string } | null>(null);

  // Custom integration state
  const [customIntegrations, setCustomIntegrations] = useState<CustomIntegration[]>([]);
  const [showCustomIntegrationForm, setShowCustomIntegrationForm] = useState(false);
  const [editingCustomIntegration, setEditingCustomIntegration] = useState<CustomIntegration | undefined>(undefined);
  const [deleteCustomModal, setDeleteCustomModal] = useState<CustomIntegration | null>(null);

  // Load all data in parallel for better performance
  // OPTIMIZED: Uses single batch API call for all statuses instead of N separate calls
  const loadData = useCallback(async () => {
    setLoading(true);
    
    try {
      // OPTIMIZED: Only 4 parallel API calls instead of 7+
      // Uses batch endpoint for all OAuth statuses (no external API calls!)
      const [serversRes, assistantsRes, statusesRes, customIntegrationsRes] = await Promise.all([
        api.getMCPServers(),
        api.listAssistants(),
        api.getAllMCPOAuthStatuses(), // Single call for all statuses
        api.listCustomIntegrations().catch(() => ({ success: false, data: [] })), // Gracefully handle if backend doesn't support yet
      ]);

      // Handle servers
      if (serversRes.success) {
        const serverData = (serversRes.data as any)?.servers || serversRes.data || [];
        setServers(Array.isArray(serverData) ? serverData : []);
      }

      // Handle assistants
      if (assistantsRes.success) {
        setAssistants(assistantsRes.data || []);
      }

      // Handle connection statuses from batch response
      if (statusesRes.success && statusesRes.data) {
        setConnectionStatus(statusesRes.data as Record<string, MCPOAuthStatus>);
      } else {
        // Fallback: initialize empty statuses
        const emptyStatuses: Record<string, MCPOAuthStatus> = {};
        INTEGRATIONS.forEach(integration => {
          emptyStatuses[integration.provider] = { connected: false, provider: integration.provider };
        });
        setConnectionStatus(emptyStatuses);
      }

      // Handle custom integrations (response may be array or paginated { items: [] })
      if (customIntegrationsRes.success && customIntegrationsRes.data) {
        const raw = customIntegrationsRes.data as any;
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.items) ? raw.items : [];
        setCustomIntegrations(list);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Listen for OAuth postMessage from popup window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'oauth-success') {
        console.log('OAuth success for:', event.data.provider);
        loadData();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [loadData]);

  // Handle ?connected= query param after OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectedProvider = params.get('connected');
    if (connectedProvider) {
      // Clear query param from URL without reload
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      // Refresh data to pick up newly connected integration
      loadData();
    }
  }, [loadData]);

  const handleConnect = (integration: IntegrationConfig) => {
    if (integration.provider === 'github') {
      setShowGithubModal(true);
    } else if (integration.authType === 'pat') {
      setShowAdoModal(true);
    } else {
      window.location.href = api.getMCPOAuthUrl(integration.provider);
    }
  };

  const handleDisconnect = async (provider: string, name: string) => {
    setDisconnectModal({ provider, name });
  };
  
  const confirmDisconnect = async () => {
    if (!disconnectModal) return;
    
    const { provider } = disconnectModal;
    
    try {
      await api.disconnectMCPProvider(provider);
      setConnectionStatus(prev => ({
        ...prev,
        [provider]: { connected: false, provider }
      }));
      setTestResults(prev => ({ ...prev, [provider]: null }));
    } catch (error) {
      console.error('Failed to disconnect:', error);
    } finally {
      setDisconnectModal(null);
    }
  };
  
  const cancelDisconnect = () => {
    setDisconnectModal(null);
  };

  const handleAdoConnect = async () => {
    if (!adoOrg || !adoPat) {
      setAdoError('Please fill in all fields');
      return;
    }

    setAdoConnecting(true);
    setAdoError('');

    try {
      const response = await api.authenticateAzureDevOps(adoOrg, adoPat);
      if (response.success) {
        setShowAdoModal(false);
        setAdoOrg('');
        setAdoPat('');
        await loadData();
      }
    } catch (error: any) {
      setAdoError(error.message || 'Failed to connect');
    } finally {
      setAdoConnecting(false);
    }
  };

  const handleGithubConnect = async () => {
    if (!githubPat) {
      setGithubError('Please enter your Personal Access Token');
      return;
    }

    setGithubConnecting(true);
    setGithubError('');

    try {
      const response = await api.authenticateGitHub(githubPat);
      if (response.success) {
        setShowGithubModal(false);
        setGithubPat('');
        await loadData();
      } else {
        setGithubError((response as any).error || 'Failed to connect');
      }
    } catch (error: any) {
      setGithubError(error.message || 'Failed to connect');
    } finally {
      setGithubConnecting(false);
    }
  };

  const handleTestConnection = async (provider: string) => {
    setTestingProvider(provider);
    // Clear previous result immediately when starting a new test
    setTestResults(prev => ({ ...prev, [provider]: null }));

    try {
      let result: MCPToolResult;
      
      switch (provider) {
        case 'jira':
          result = (await api.executeMCPTool('jira', 'jira_get_projects', {})).data;
          break;
        case 'confluence':
          result = (await api.executeMCPTool('confluence', 'confluence_get_spaces', { limit: 5 })).data;
          break;
        case 'azure-devops':
          result = (await api.executeMCPTool('azure-devops', 'ado_search_work_items', { 
            query: "SELECT [System.Id], [System.Title] FROM WorkItems ORDER BY [System.CreatedDate] DESC",
          })).data;
          break;
        case 'github':
          result = (await api.executeMCPTool('github', 'github_list_repositories', { perPage: 5 })).data;
          break;
        case 'slack':
          result = (await api.executeMCPTool('slack', 'slack_list_channels', { limit: 5 })).data;
          break;
        default:
          result = { success: false, error: 'Unknown provider' };
      }

      setTestResults(prev => ({ ...prev, [provider]: result }));
      
      // Auto-clear success results after 5 seconds
      if (result.success) {
        setTimeout(() => {
          setTestResults(prev => {
            const current = prev[provider];
            // Only clear if still showing success (not replaced by another test)
            if (current?.success) {
              return { ...prev, [provider]: null };
            }
            return prev;
          });
        }, 5000);
      }
    } catch (error: any) {
      setTestResults(prev => ({
        ...prev,
        [provider]: { success: false, error: error.message || 'Test failed' },
      }));
    } finally {
      setTestingProvider(null);
    }
  };

  const handleSyncToAssistants = async (provider: string) => {
    const assistantIds = selectedAssistants[provider] || [];
    if (assistantIds.length === 0) return;

    setSyncingProvider(provider);
    try {
      await Promise.all(
        assistantIds.map(id => api.triggerIntegrationSync(provider, id, { type: 'full' }))
      );
      // Show success feedback
    } catch (error: any) {
      console.error('Sync failed:', error);
    } finally {
      setSyncingProvider(null);
    }
  };

  const toggleAssistantSelection = (provider: string, assistantId: string) => {
    setSelectedAssistants(prev => {
      const current = prev[provider] || [];
      const updated = current.includes(assistantId)
        ? current.filter(id => id !== assistantId)
        : [...current, assistantId];
      return { ...prev, [provider]: updated };
    });
  };

  const getServerInfo = (provider: string): MCPServer | undefined => {
    if (!servers || !Array.isArray(servers)) return undefined;
    return servers.find(s => s.name === provider);
  };

  // Custom integration handlers
  const handleAddCustomIntegration = () => {
    setEditingCustomIntegration(undefined);
    setShowCustomIntegrationForm(true);
  };

  const handleEditCustomIntegration = (integration: CustomIntegration) => {
    setEditingCustomIntegration(integration);
    setShowCustomIntegrationForm(true);
  };

  const handleSaveCustomIntegration = async (integration: CustomIntegration) => {
    setShowCustomIntegrationForm(false);
    setEditingCustomIntegration(undefined);
    // Reload data to get updated list
    await loadData();
  };

  const handleDeleteCustomIntegration = (integration: CustomIntegration) => {
    setDeleteCustomModal(integration);
  };

  const confirmDeleteCustomIntegration = async () => {
    if (!deleteCustomModal) return;

    try {
      await api.deleteCustomIntegration(deleteCustomModal.id);
      setCustomIntegrations(prev => prev.filter(i => i.id !== deleteCustomModal.id));
    } catch (error) {
      console.error('Failed to delete custom integration:', error);
    } finally {
      setDeleteCustomModal(null);
    }
  };

  const cancelDeleteCustomIntegration = () => {
    setDeleteCustomModal(null);
  };

  // Filter integrations
  const filteredIntegrations = INTEGRATIONS.filter(integration => {
    const matchesSearch = !searchTerm || 
      integration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      integration.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || integration.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const builtInConnectedCount = Object.values(connectionStatus).filter(s => s?.connected).length;
  const customList = Array.isArray(customIntegrations) ? customIntegrations : [];
  const customConnectedCount = customList.filter(i => i.status === 'connected').length;
  const connectedCount = builtInConnectedCount + customConnectedCount;

  // Sidebar content for AppLayout (matching Chats/Assistants pattern)
  const integrationsSidebar = (
    <KnowledgeSidebar
      headerAction={
        <Button
          type="button"
          variant="primary"
          size="sm"
          leftIcon={<Plus size={16} />}
          onClick={handleAddCustomIntegration}
        >
          Create
        </Button>
      }
    >
      <div className={styles.sidebarFilters}>
        <h3 className={styles.sidebarSectionTitle}>FILTERS</h3>

        <div className={styles.sidebarFilterGroup}>
          <Input
            isSearch
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
          />
        </div>

        <div className={styles.sidebarFilterGroup}>
          <button className={styles.sidebarFilterHeader}>
            <ChevronDown size={14} />
            <span>PROJECT</span>
          </button>
          <div className={styles.sidebarFilterContent}>
            <select className={styles.sidebarFilterSelect}>
              <option value="">Project</option>
            </select>
          </div>
        </div>

        <div className={styles.sidebarFilterGroup}>
          <button className={styles.sidebarFilterHeader}>
            <ChevronDown size={14} />
            <span>TYPE</span>
          </button>
          <div className={styles.sidebarFilterContent}>
            <select
              className={styles.sidebarFilterSelect}
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value || null)}
            >
              <option value="">Type</option>
              {Object.entries(CATEGORIES).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.sidebarFilterGroup}>
          <button className={styles.sidebarFilterHeader}>
            <ChevronDown size={14} />
            <span>GLOBAL</span>
          </button>
          <div className={styles.sidebarFilterContent}>
            <div className={styles.radioGroup}>
              <label className={styles.radioItem}>
                <input type="radio" name="global" defaultChecked /> All
              </label>
              <label className={styles.radioItem}>
                <input type="radio" name="global" /> Global
              </label>
              <label className={styles.radioItem}>
                <input type="radio" name="global" /> Non Global
              </label>
            </div>
          </div>
        </div>
      </div>
    </KnowledgeSidebar>
  );

  if (loading) {
    return (
      <AppLayout sidebar={integrationsSidebar} hideLayout={embedded}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingContent}>
            <Spinner size="lg" />
            <h2>Loading Integrations</h2>
            <p>Connecting to your services...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout sidebar={integrationsSidebar} hideLayout={embedded}>
      <div className={styles.page}>
        {/* Integration Stats Header */}
        <div className={styles.statsBar}>
          <span className={styles.statItem}><strong>{connectedCount}</strong> Connected</span>
          <span className={styles.statItem}><strong>{INTEGRATIONS.length + customIntegrations.length}</strong> Available</span>
        </div>

        {/* Custom Integrations Section — always visible */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2>Custom Integrations</h2>
            {customList.length > 0 && (
              <span className={styles.sectionCount}>{customList.length}</span>
            )}
          </div>
          <div className={styles.grid}>
            {/* Add Custom Integration Card */}
            <div
              className={`${styles.card} ${styles.addCustomCard}`}
              onClick={handleAddCustomIntegration}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleAddCustomIntegration()}
            >
              <div className={styles.addCustomCardContent}>
                <div className={styles.addCustomIcon}>
                  <Plus size={28} />
                </div>
                <h3>Add Custom Integration</h3>
                <p>Connect any API service with your own credentials and define MCP tools</p>
              </div>
            </div>

            {customList.map((integration) => {
              const isConnected = integration.status === 'connected';
              
              return (
                <div 
                  key={integration.id} 
                  className={`${styles.card} ${isConnected ? styles.connected : ''} ${styles.customCard}`}
                >
                  {/* Custom Badge */}
                  <div className={styles.customBadge}>
                    <span>Custom</span>
                  </div>

                  {/* Connected Badge */}
                  {isConnected && (
                    <div className={styles.connectedBadge}>
                      <CheckCircle2 size={14} />
                      <span>Connected</span>
                    </div>
                  )}

                  {/* Card Header */}
                  <div className={styles.cardHeader}>
                    <div 
                      className={styles.cardIcon}
                      style={{ 
                        background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
                      }}
                    >
                      {integration.iconUrl ? (
                        <img src={integration.iconUrl} alt={integration.name} style={{ width: 24, height: 24 }} />
                      ) : (
                        <Link2 size={24} />
                      )}
                    </div>
                    <div className={styles.cardInfo}>
                      <h3>{integration.name}</h3>
                      <span className={styles.categoryTag}>
                        <Shield size={14} />
                        {integration.authType === 'oauth2' ? 'OAuth 2.0' : integration.authType === 'api-key' ? 'API Key' : 'Basic Auth'}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className={styles.cardDescription}>
                    {integration.description || integration.baseUrl}
                  </p>
                  {(integration.toolCount > 0 || (integration.tools && integration.tools.length > 0)) && (
                    <span className={styles.categoryTag} style={{ marginBottom: 'var(--space-2)' }}>
                      {integration.toolCount || integration.tools?.length || 0} MCP tools
                    </span>
                  )}

                  {/* Actions */}
                  <div className={styles.cardActions}>
                    <button
                      className={styles.btnSecondary}
                      onClick={() => handleEditCustomIntegration(integration)}
                    >
                      <Edit2 size={16} />
                      <span>Edit</span>
                    </button>
                    <button
                      className={styles.btnDanger}
                      onClick={() => handleDeleteCustomIntegration(integration)}
                    >
                      <Trash2 size={16} />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pre-configured Integrations */}
        <div className={styles.section}>
          {customList.length > 0 && (
            <div className={styles.sectionHeader}>
              <h2>Pre-configured Integrations</h2>
              <span className={styles.sectionCount}>{INTEGRATIONS.length}</span>
            </div>
          )}

        {/* Integration Cards */}
        <div className={styles.grid}>
          {filteredIntegrations.map((integration) => {
            const status = connectionStatus[integration.provider];
            const server = getServerInfo(integration.provider);
            const isConnected = status?.connected;
            const testResult = testResults[integration.provider];
            const isTesting = testingProvider === integration.provider;
            const isSyncing = syncingProvider === integration.provider;
            const isExpanded = expandedCard === integration.provider;
            const selectedCount = (selectedAssistants[integration.provider] || []).length;

            return (
              <div 
                key={integration.provider} 
                className={`${styles.card} ${isConnected ? styles.connected : ''}`}
              >
                {/* Connected Badge */}
                {isConnected && (
                  <div className={styles.connectedBadge}>
                    <CheckCircle2 size={14} />
                    <span>Connected</span>
                  </div>
                )}

                {/* Card Header */}
                <div className={styles.cardHeader}>
                  <div 
                    className={styles.cardIcon}
                    style={{ background: integration.bgGradient }}
                  >
                    {integration.icon}
                  </div>
                  <div className={styles.cardInfo}>
                    <h3>{integration.name}</h3>
                    <span className={styles.categoryTag}>
                      {CATEGORIES[integration.category].icon}
                      {CATEGORIES[integration.category].label}
                    </span>
                  </div>
                </div>

                {/* Description */}
                <p className={styles.cardDescription}>{integration.description}</p>

                {/* Features */}
                <div className={styles.features}>
                  {integration.features.map(feature => (
                    <span key={feature} className={styles.featureTag}>{feature}</span>
                  ))}
                </div>

                {/* Server Info */}
                {server && isConnected && (
                  <div className={styles.serverInfo}>
                    <Zap size={14} />
                    <span>{server.toolCount} tools available</span>
                    <span className={styles.dot}>•</span>
                    <span>v{server.version}</span>
                  </div>
                )}

                {/* Connected Account Info */}
                {isConnected && status?.accountInfo && (
                  <div className={styles.accountInfo}>
                    {status.accountInfo.avatarUrl && (
                      <img 
                        src={status.accountInfo.avatarUrl} 
                        alt="" 
                        className={styles.avatar}
                      />
                    )}
                    <div className={styles.accountDetails}>
                      <span className={styles.accountName}>
                        {status.accountInfo.name || status.accountInfo.email}
                      </span>
                      {status.accountInfo.siteUrl && (
                        <a 
                          href={status.accountInfo.siteUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={styles.siteLink}
                        >
                          {new URL(status.accountInfo.siteUrl).hostname}
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Expandable Sync Section */}
                {isConnected && (
                  <div className={styles.expandSection}>
                    <button
                      className={styles.expandButton}
                      onClick={() => setExpandedCard(isExpanded ? null : integration.provider)}
                    >
                      <Settings2 size={16} />
                      <span>Configure Sync</span>
                      <ChevronDown 
                        size={16} 
                        className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`}
                      />
                    </button>
                    
                    {isExpanded && (
                      <div className={styles.expandContent}>
                        <p className={styles.syncDescription}>
                          Select assistants to sync {integration.name} data for context-aware responses.
                        </p>
                        <div className={styles.assistantList}>
                          {assistants.length === 0 ? (
                            <div className={styles.emptyAssistants}>
                              No assistants available. Create one first.
                            </div>
                          ) : (
                            assistants.map(assistant => (
                              <label key={assistant.id} className={styles.assistantItem}>
                                <input
                                  type="checkbox"
                                  checked={(selectedAssistants[integration.provider] || []).includes(assistant.id)}
                                  onChange={() => toggleAssistantSelection(integration.provider, assistant.id)}
                                />
                                <span className={styles.checkmark}>
                                  <Check size={12} />
                                </span>
                                <span className={styles.assistantName}>{assistant.name}</span>
                              </label>
                            ))
                          )}
                        </div>
                        {selectedCount > 0 && (
                          <button
                            className={styles.syncButton}
                            onClick={() => handleSyncToAssistants(integration.provider)}
                            disabled={isSyncing}
                          >
                            {isSyncing ? (
                              <>
                                <Spinner size="sm" />
                                Syncing...
                              </>
                            ) : (
                              <>
                                <RefreshCw size={16} />
                                Sync to {selectedCount} Assistant{selectedCount > 1 ? 's' : ''}
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Test Result */}
                {testResult && (
                  <div className={`${styles.testResult} ${testResult.success ? styles.success : styles.error}`}>
                    {testResult.success ? (
                      <>
                        <CheckCircle2 size={16} />
                        <span>Connection verified successfully</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={16} />
                        <span>{testResult.error}</span>
                      </>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className={styles.cardActions}>
                  {isConnected ? (
                    <>
                      <button
                        className={styles.btnSecondary}
                        onClick={() => handleTestConnection(integration.provider)}
                        disabled={isTesting}
                      >
                        {isTesting ? (
                          <Spinner size="sm" />
                        ) : (
                          <Zap size={16} />
                        )}
                        <span>{isTesting ? 'Testing...' : 'Test'}</span>
                      </button>
                      <button
                        className={styles.btnDanger}
                        onClick={() => handleDisconnect(integration.provider, integration.name)}
                      >
                        <Unplug size={16} />
                        <span>Disconnect</span>
                      </button>
                    </>
                  ) : (
                    <button
                      className={styles.btnPrimary}
                      onClick={() => handleConnect(integration)}
                    >
                      <Link2 size={16} />
                      <span>Connect {integration.name}</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>

        {/* Azure DevOps Modal */}
        {showAdoModal && (
          <div className={styles.modalOverlay} onClick={() => setShowAdoModal(false)}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <div className={styles.modalIcon} style={{ background: 'linear-gradient(135deg, #0078D4 0%, #00BCF2 100%)' }}>
                  <Shield size={24} />
                </div>
                <div>
                  <h2>Connect Azure DevOps</h2>
                  <p>Enter your organization details and Personal Access Token</p>
                </div>
                <button className={styles.modalClose} onClick={() => setShowAdoModal(false)}>×</button>
              </div>
              
              <div className={styles.modalBody}>
                <div className={styles.formGroup}>
                  <label>Organization Name</label>
                  <input
                    type="text"
                    placeholder="e.g., myorganization"
                    value={adoOrg}
                    onChange={(e) => setAdoOrg(e.target.value)}
                    className={styles.input}
                  />
                  <span className={styles.hint}>
                    Found in your URL: https://dev.azure.com/<strong>organization</strong>
                  </span>
                </div>

                <div className={styles.formGroup}>
                  <label>Personal Access Token</label>
                  <input
                    type="password"
                    placeholder="Enter your PAT"
                    value={adoPat}
                    onChange={(e) => setAdoPat(e.target.value)}
                    className={styles.input}
                  />
                  <span className={styles.hint}>
                    <a 
                      href="https://dev.azure.com/_usersSettings/tokens" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Create a PAT with full access →
                    </a>
                  </span>
                </div>

                {adoError && (
                  <div className={styles.formError}>
                    <AlertCircle size={16} />
                    {adoError}
                  </div>
                )}
              </div>
              
              <div className={styles.modalFooter}>
                <button 
                  className={styles.btnSecondary} 
                  onClick={() => setShowAdoModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className={styles.btnPrimary} 
                  onClick={handleAdoConnect}
                  disabled={adoConnecting}
                >
                  {adoConnecting ? (
                    <>
                      <Spinner size="sm" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Link2 size={16} />
                      Connect
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* GitHub Modal */}
        {showGithubModal && (
          <div className={styles.modalOverlay} onClick={() => setShowGithubModal(false)}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <div className={styles.modalIcon} style={{ background: 'linear-gradient(135deg, #24292E 0%, #4A4A4A 100%)' }}>
                  <GitHubIcon size={24} />
                </div>
                <div>
                  <h2>Connect GitHub</h2>
                  <p>Enter a Personal Access Token to connect your GitHub account</p>
                </div>
                <button className={styles.modalClose} onClick={() => setShowGithubModal(false)}>×</button>
              </div>

              <div className={styles.modalBody}>
                <div className={styles.formGroup}>
                  <label>Personal Access Token</label>
                  <input
                    type="password"
                    placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                    value={githubPat}
                    onChange={(e) => setGithubPat(e.target.value)}
                    className={styles.input}
                  />
                  <span className={styles.hint}>
                    <a
                      href="https://github.com/settings/tokens/new?scopes=repo,read:user,read:org,workflow&description=BringIQ"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Create a token with repo, read:user, read:org and workflow scopes →
                    </a>
                  </span>
                </div>

                {githubError && (
                  <div className={styles.formError}>
                    <AlertCircle size={16} />
                    {githubError}
                  </div>
                )}
              </div>

              <div className={styles.modalFooter}>
                <button
                  className={styles.btnSecondary}
                  onClick={() => setShowGithubModal(false)}
                >
                  Cancel
                </button>
                <button
                  className={styles.btnPrimary}
                  onClick={handleGithubConnect}
                  disabled={githubConnecting}
                >
                  {githubConnecting ? (
                    <>
                      <Spinner size="sm" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Link2 size={16} />
                      Connect
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Disconnect Confirmation Modal */}
        <ConfirmModal
          isOpen={!!disconnectModal}
          title="Disconnect Integration"
          message={`Are you sure you want to disconnect ${disconnectModal?.name}? You will need to re-authenticate to use this integration again.`}
          confirmText="Disconnect"
          cancelText="Cancel"
          variant="danger"
          onConfirm={confirmDisconnect}
          onClose={cancelDisconnect}
        />

        {/* Custom Integration Form Modal */}
        {showCustomIntegrationForm && (
          <CustomIntegrationForm
            integration={editingCustomIntegration}
            onSave={handleSaveCustomIntegration}
            onClose={() => {
              setShowCustomIntegrationForm(false);
              setEditingCustomIntegration(undefined);
            }}
          />
        )}

        {/* Delete Custom Integration Confirmation Modal */}
        <ConfirmModal
          isOpen={!!deleteCustomModal}
          title="Delete Custom Integration"
          message={`Are you sure you want to delete "${deleteCustomModal?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          onConfirm={confirmDeleteCustomIntegration}
          onClose={cancelDeleteCustomIntegration}
        />
      </div>
    </AppLayout>
  );
};

export default IntegrationsPage;
